import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from './ui/hover-card';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
  Settings2,
  Filter,
  X,
  GripVertical,
  Trash2,
} from 'lucide-react';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

/**
 * Advanced Data Grid Component
 * 
 * Features:
 * - Sort on any field
 * - Filter on any field  
 * - Column reordering (drag & drop)
 * - Column visibility toggle
 * - Row selection with bulk actions
 * - Pagination with jump to page
 * 
 * @param {Object[]} columns - Column definitions with { key, label, sortable, filterable, filterType, visible, required, render }
 * @param {Object[]} data - Array of data objects
 * @param {Function} onRowClick - Callback when row is clicked
 * @param {Function} renderRowActions - Render function for row actions column
 * @param {Object[]} selectedIds - Set of selected row IDs
 * @param {Function} onSelectionChange - Callback when selection changes
 * @param {Function} onBulkDelete - Callback for bulk delete action
 * @param {string} rowIdKey - Key to use for row ID (default: 'id')
 * @param {Function} getRowValue - Custom function to get display value for a cell
 * @param {string} emptyMessage - Message when no data
 * @param {React.ReactNode} emptyIcon - Icon for empty state
 * @param {Function} onHoverOpen - Callback when hover card opens (row, columnKey, open)
 * @param {boolean} showFilters - Whether to show the filter row (default: true)
 */
export default function AdvancedDataGrid({
  columns: initialColumns,
  data,
  onRowClick,
  renderRowActions,
  renderRowHoverContent,
  selectedIds = new Set(),
  onSelectionChange,
  onBulkDelete,
  rowIdKey = 'id',
  getRowValue,
  emptyMessage = 'No data found',
  emptyIcon,
  loading = false,
  savedPreferences = null,
  onColumnChange = null,
  onHoverOpen = null,
  showFilters = true,
  defaultSort = null,   // e.g. { key: 'last_activity_at', direction: 'desc' }
  storageKey = null,    // e.g. 'jtcrm_leads_col_filters' — persists column filters in sessionStorage
  onFilteredDataChange = null, // Callback with filtered data array when column filters change
}) {
  // Column state - order and visibility (use saved preferences if available)
  // Merge saved preferences with any new columns that weren't in saved prefs
  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = savedPreferences?.columnOrder;
    if (!savedOrder) return initialColumns.map(c => c.key);
    // Add any new columns that aren't in saved order
    const newColumns = initialColumns.map(c => c.key).filter(k => !savedOrder.includes(k));
    return [...savedOrder, ...newColumns];
  });
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const savedVisible = savedPreferences?.visibleColumns;
    if (!savedVisible) return initialColumns.filter(c => c.visible !== false).map(c => c.key);
    // Add any new columns that aren't in saved visibility (show them by default)
    const allKeys = initialColumns.map(c => c.key);
    const newColumns = allKeys.filter(k => !savedVisible.includes(k) && initialColumns.find(c => c.key === k)?.visible !== false);
    return [...savedVisible, ...newColumns];
  });
  
  // Column widths state (saved per column)
  const [columnWidths, setColumnWidths] = useState(() => {
    return savedPreferences?.columnWidths || {};
  });
  
  // Resize state - using refs to avoid stale closures in event listeners
  const [resizingColumn, setResizingColumn] = useState(null);
  const resizeRef = React.useRef({
    column: null,
    startX: 0,
    startWidth: 0
  });
  
  // Sort state — start from defaultSort if provided
  const [sortConfig, setSortConfig] = useState(defaultSort || { key: null, direction: 'asc' });
  
  // Filter state — restored from sessionStorage if storageKey is provided
  const [columnFilters, setColumnFilters] = useState(() => {
    if (!storageKey) return {};
    try {
      return JSON.parse(sessionStorage.getItem(storageKey) || '{}');
    } catch {
      return {};
    }
  });

  // Persist column filters to sessionStorage whenever they change
  useEffect(() => {
    if (!storageKey) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(columnFilters));
    } catch {
      // sessionStorage unavailable — ignore
    }
  }, [storageKey, columnFilters]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [jumpToPage, setJumpToPage] = useState('');
  
  // Drag state for column reordering
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // Get columns in current order with visibility
  const orderedColumns = useMemo(() => {
    return columnOrder
      .filter(key => visibleColumns.includes(key))
      .map(key => initialColumns.find(c => c.key === key))
      .filter(Boolean);
  }, [columnOrder, visibleColumns, initialColumns]);

  // Default value getter
  const defaultGetValue = useCallback((row, key) => {
    const value = row[key];
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? 'yes' : 'no';
    return String(value).toLowerCase();
  }, []);

  const getValue = getRowValue || defaultGetValue;

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let result = [...data];
    
    // Apply column filters
    Object.entries(columnFilters).forEach(([key, filterValue]) => {
      if (filterValue && filterValue !== '__all__') {
        const column = initialColumns.find(c => c.key === key);
        
        result = result.filter(row => {
          // Use column's custom getValue if available, otherwise use default
          const value = column?.getValue ? column.getValue(row) : getValue(row, key);
          
          // Boolean filter
          if (column?.filterType === 'boolean') {
            if (filterValue === 'yes' || filterValue === 'true') {
              return row[key] === true;
            } else if (filterValue === 'no' || filterValue === 'false') {
              return !row[key];
            }
            return true;
          }

          // Status filter (exact match) — use column.getValue if provided (handles nested fields)
          if (column?.filterType === 'status') {
            const rawValue = column?.getValue ? column.getValue(row) : row[key];
            return rawValue === filterValue;
          }
          
          // Text filter (contains)
          return value.includes(filterValue.toLowerCase());
        });
      }
    });
    
    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const column = initialColumns.find(c => c.key === sortConfig.key);
        
        // Use column's custom getValue if available, otherwise use default
        let aValue, bValue;
        if (column?.getValue) {
          aValue = column.getValue(a);
          bValue = column.getValue(b);
        } else {
          aValue = getValue(a, sortConfig.key);
          bValue = getValue(b, sortConfig.key);
        }
        
        // Handle date sorting
        if (column?.sortType === 'date' || sortConfig.key.includes('_at')) {
          aValue = new Date(a[sortConfig.key] || 0).getTime();
          bValue = new Date(b[sortConfig.key] || 0).getTime();
        }
        
        // Handle numeric sorting
        if (column?.sortType === 'number') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [data, columnFilters, sortConfig, getValue, initialColumns]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = filteredAndSortedData.slice(startIndex, endIndex);
  const hasActiveFilters = Object.keys(columnFilters).length > 0;

  // Notify parent when column filters change the visible data set
  // Use a ref to avoid re-render loops — only call back when filter keys actually change
  const prevFilterKeyRef = React.useRef('');
  useEffect(() => {
    if (!onFilteredDataChange) return;
    const filterKey = JSON.stringify(columnFilters);
    if (filterKey === prevFilterKeyRef.current) return;
    prevFilterKeyRef.current = filterKey;
    onFilteredDataChange(hasActiveFilters ? filteredAndSortedData : null);
  }, [columnFilters, hasActiveFilters, filteredAndSortedData, onFilteredDataChange]);

  // Selection helpers
  const isAllOnPageSelected = paginatedData.length > 0 && paginatedData.every(row => selectedIds.has(row[rowIdKey]));
  const isSomeOnPageSelected = paginatedData.some(row => selectedIds.has(row[rowIdKey])) && !isAllOnPageSelected;

  // Handlers
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleColumnFilter = (key, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  const clearColumnFilter = (key) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setColumnFilters({});
    setCurrentPage(1);
    if (storageKey) {
      try { sessionStorage.removeItem(storageKey); } catch {}
    }
  };

  const toggleColumnVisibility = (columnKey) => {
    const column = initialColumns.find(c => c.key === columnKey);
    if (column?.required) return;
    
    setVisibleColumns(prev => {
      const newVisible = prev.includes(columnKey) 
        ? prev.filter(k => k !== columnKey)
        : [...prev, columnKey];
      
      // Notify parent of change (include column widths)
      if (onColumnChange) {
        onColumnChange(newVisible, columnOrder, columnWidths);
      }
      return newVisible;
    });
  };

  const toggleSelectAll = () => {
    if (!onSelectionChange) return;
    
    const newSelection = new Set(selectedIds);
    if (isAllOnPageSelected) {
      paginatedData.forEach(row => newSelection.delete(row[rowIdKey]));
    } else {
      paginatedData.forEach(row => newSelection.add(row[rowIdKey]));
    }
    onSelectionChange(newSelection);
  };

  const toggleSelectRow = (rowId) => {
    if (!onSelectionChange) return;
    
    const newSelection = new Set(selectedIds);
    if (newSelection.has(rowId)) {
      newSelection.delete(rowId);
    } else {
      newSelection.add(rowId);
    }
    onSelectionChange(newSelection);
  };

  // Column drag handlers
  const handleDragStart = (e, columnKey) => {
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, columnKey) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== columnKey) {
      setDragOverColumn(columnKey);
    }
  };

  const handleDragEnd = () => {
    if (draggedColumn && dragOverColumn && draggedColumn !== dragOverColumn) {
      const newOrder = [...columnOrder];
      const draggedIdx = newOrder.indexOf(draggedColumn);
      const dropIdx = newOrder.indexOf(dragOverColumn);
      
      newOrder.splice(draggedIdx, 1);
      newOrder.splice(dropIdx, 0, draggedColumn);
      
      setColumnOrder(newOrder);
      
      // Notify parent of change (include column widths)
      if (onColumnChange) {
        onColumnChange(visibleColumns, newOrder, columnWidths);
      }
    }
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  // Column resize handlers - using refs to avoid stale closures and circular dependencies
  const handleResizeMoveRef = React.useRef(null);
  const handleResizeEndRef = React.useRef(null);
  
  handleResizeMoveRef.current = (e) => {
    const { column, startX, startWidth } = resizeRef.current;
    if (!column) return;
    
    const diff = e.clientX - startX;
    const newWidth = Math.max(80, Math.min(500, startWidth + diff)); // Min 80px, Max 500px
    
    setColumnWidths(prev => ({
      ...prev,
      [column]: newWidth
    }));
  };

  handleResizeEndRef.current = () => {
    const { column } = resizeRef.current;
    
    if (column) {
      // Get the latest columnWidths by using functional update pattern
      setColumnWidths(currentWidths => {
        // Save the widths after state is updated
        if (onColumnChange) {
          // Use setTimeout to ensure state is settled
          setTimeout(() => {
            onColumnChange(visibleColumns, columnOrder, currentWidths);
          }, 0);
        }
        return currentWidths;
      });
    }
    
    resizeRef.current = { column: null, startX: 0, startWidth: 0 };
    setResizingColumn(null);
    
    document.removeEventListener('mousemove', handleResizeMoveFn);
    document.removeEventListener('mouseup', handleResizeEndFn);
  };

  // Stable function references that delegate to refs
  const handleResizeMoveFn = useCallback((e) => {
    handleResizeMoveRef.current?.(e);
  }, []);

  const handleResizeEndFn = useCallback(() => {
    handleResizeEndRef.current?.();
  }, []);

  const handleResizeStart = useCallback((e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    
    const colDef = initialColumns.find(c => c.key === columnKey);
    const currentWidth = columnWidths[columnKey] || colDef?.defaultWidth || 150;
    
    // Store in ref for access during mousemove/mouseup
    resizeRef.current = {
      column: columnKey,
      startX: e.clientX,
      startWidth: currentWidth
    };
    
    setResizingColumn(columnKey);
    
    // Add mouse event listeners
    document.addEventListener('mousemove', handleResizeMoveFn);
    document.addEventListener('mouseup', handleResizeEndFn);
  }, [columnWidths, handleResizeMoveFn, handleResizeEndFn]);

  // Cleanup resize listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMoveFn);
      document.removeEventListener('mouseup', handleResizeEndFn);
    };
  }, [handleResizeMoveFn, handleResizeEndFn]);

  // Pagination handlers
  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPrevPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(totalPages, prev + 1));

  const handleJumpToPage = (e) => {
    e.preventDefault();
    const pageNum = parseInt(jumpToPage, 10);
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
      setJumpToPage('');
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(parseInt(newSize, 10));
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Render filter input based on type
  const renderFilterInput = (column) => {
    if (!column.filterable) return null;

    if (column.filterType === 'boolean') {
      return (
        <Select
          value={columnFilters[column.key] || '__all__'}
          onValueChange={(value) => handleColumnFilter(column.key, value === '__all__' ? '' : value)}
        >
          <SelectTrigger className="h-8 text-xs" data-testid={`filter-${column.key}`}>
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All</SelectItem>
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    if (column.filterType === 'status' && column.filterOptions) {
      return (
        <Select
          value={columnFilters[column.key] || '__all__'}
          onValueChange={(value) => handleColumnFilter(column.key, value === '__all__' ? '' : value)}
        >
          <SelectTrigger className="h-8 text-xs" data-testid={`filter-${column.key}`}>
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All</SelectItem>
            {column.filterOptions.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    return (
      <div className="relative">
        <Input
          placeholder={`Filter...`}
          value={columnFilters[column.key] || ''}
          onChange={(e) => handleColumnFilter(column.key, e.target.value)}
          className="h-8 text-xs pr-8"
          data-testid={`filter-${column.key}`}
        />
        {columnFilters[column.key] && (
          <button
            onClick={() => clearColumnFilter(column.key)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className={cn(
      "border rounded-lg bg-white",
      resizingColumn && "select-none cursor-col-resize"
    )}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-3">
          {selectedIds.size > 0 ? (
            <>
              <span className="text-sm font-medium text-indigo-700">
                {selectedIds.size} selected
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSelectionChange?.(new Set())}
                className="text-indigo-600 hover:text-indigo-700"
              >
                Clear
              </Button>
              {onBulkDelete && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={onBulkDelete}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected
                </Button>
              )}
            </>
          ) : (
            <span className="text-sm text-slate-500">
              {filteredAndSortedData.length} items
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="text-amber-600 hover:text-amber-700"
            >
              <Filter className="w-4 h-4 mr-1" />
              Clear Filters
            </Button>
          )}
          
          {/* Column Visibility Toggle */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="columns-toggle">
                <Settings2 className="w-4 h-4 mr-2" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span>Toggle Columns</span>
                <span className="text-xs text-slate-500 font-normal">Drag to reorder</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {columnOrder.map((colKey, index) => {
                const col = initialColumns.find(c => c.key === colKey);
                if (!col) return null;
                return (
                  <div
                    key={col.key}
                    draggable={!col.required}
                    onDragStart={(e) => handleDragStart(e, col.key)}
                    onDragOver={(e) => handleDragOver(e, col.key)}
                    onDragEnd={handleDragEnd}
                    className={cn(
                      "flex items-center gap-2 px-2 py-2 rounded cursor-move",
                      dragOverColumn === col.key && "bg-blue-50 border-l-2 border-blue-400",
                      !col.required && "hover:bg-slate-50"
                    )}
                  >
                    {!col.required && (
                      <GripVertical className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    )}
                    <DropdownMenuCheckboxItem
                      checked={visibleColumns.includes(col.key)}
                      onCheckedChange={() => toggleColumnVisibility(col.key)}
                      disabled={col.required}
                      className="flex-1 cursor-pointer"
                      onSelect={(e) => e.preventDefault()}
                    >
                      {col.label} {col.required && <span className="text-slate-400 ml-1 text-xs">(required)</span>}
                    </DropdownMenuCheckboxItem>
                  </div>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Active Filters Indicator */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-100">
          <Filter className="w-4 h-4 text-amber-600" />
          <span className="text-sm text-amber-700">
            {Object.keys(columnFilters).length} filter(s) active
          </span>
          <div className="flex items-center gap-1 ml-2">
            {Object.entries(columnFilters).map(([key, value]) => (
              <span 
                key={key}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded"
              >
                {initialColumns.find(c => c.key === key)?.label}: {value}
                <button onClick={() => clearColumnFilter(key)} className="hover:text-amber-900">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      {data.length === 0 ? (
        <div className="text-center py-12">
          {emptyIcon}
          <p className="text-slate-500 mt-2">{emptyMessage}</p>
        </div>
      ) : (
        <>
          <div className="overflow-auto scroll-smooth scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100" style={{ maxHeight: 'calc(100vh - 280px)' }}>
            <Table className="w-full" style={{ minWidth: Math.min(orderedColumns.length * 150 + 100, 1400) }}>
              <TableHeader className="sticky top-0 z-10 shadow-[0_2px_4px_-1px_rgba(0,0,0,0.1)]">
                {/* Header Row */}
                <TableRow className="bg-slate-50 border-b-0">
                  {onSelectionChange && (
                    <TableHead className="w-12 bg-slate-50">
                      <Checkbox
                        checked={isAllOnPageSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = isSomeOnPageSelected;
                        }}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                  )}
                  {orderedColumns.map((col) => (
                    <TableHead
                      key={col.key}
                      draggable={!col.required && !resizingColumn}
                      onDragStart={(e) => handleDragStart(e, col.key)}
                      onDragOver={(e) => handleDragOver(e, col.key)}
                      onDragEnd={handleDragEnd}
                      style={{ width: columnWidths[col.key] ? `${columnWidths[col.key]}px` : col.defaultWidth ? `${col.defaultWidth}px` : 'auto', minWidth: '80px', position: 'relative' }}
                      className={cn(
                        "bg-slate-50 text-blue-700 font-bold transition-colors select-none whitespace-nowrap",
                        col.sortable && "cursor-pointer hover:bg-blue-50 active:bg-blue-100",
                        dragOverColumn === col.key && "bg-blue-100 border-l-2 border-blue-400",
                        resizingColumn === col.key && "bg-blue-100"
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (col.sortable && !resizingColumn) handleSort(col.key);
                      }}
                    >
                      <div className="flex items-center gap-2 pr-4">
                        {!col.required && (
                          <GripVertical className="w-3 h-3 text-slate-400 cursor-grab flex-shrink-0" />
                        )}
                        <span className="text-blue-700 font-bold truncate">{col.label}</span>
                        {col.sortable && (
                          <span className="text-blue-400 flex-shrink-0">
                            {sortConfig.key === col.key ? (
                              sortConfig.direction === 'asc' ? (
                                <ArrowUp className="w-4 h-4" />
                              ) : (
                                <ArrowDown className="w-4 h-4" />
                              )
                            ) : (
                              <ArrowUpDown className="w-4 h-4 opacity-50" />
                            )}
                          </span>
                        )}
                      </div>
                      {/* Resize Handle - wider hit area for easier grabbing */}
                      <div
                        className={cn(
                          "absolute right-0 top-0 bottom-0 w-2 cursor-col-resize group",
                          "hover:bg-blue-400/50 transition-colors",
                          resizingColumn === col.key && "bg-blue-500"
                        )}
                        onMouseDown={(e) => handleResizeStart(e, col.key)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`resize-handle-${col.key}`}
                      >
                        {/* Visual indicator line */}
                        <div className={cn(
                          "absolute right-0 top-0 bottom-0 w-0.5 bg-slate-300",
                          "group-hover:bg-blue-500 transition-colors",
                          resizingColumn === col.key && "bg-blue-600"
                        )} />
                      </div>
                    </TableHead>
                  ))}
                  {renderRowActions && (
                    <TableHead className="w-12 bg-slate-50 text-blue-700 font-bold">Actions</TableHead>
                  )}
                </TableRow>
                
                {/* Filter Row - Conditionally rendered */}
                {showFilters && (
                  <TableRow className="bg-slate-50 border-b border-slate-200">
                    {onSelectionChange && <TableHead className="py-1.5 bg-slate-50" />}
                    {orderedColumns.map((col) => (
                      <TableHead
                        key={`filter-${col.key}`}
                        className="py-1.5 bg-slate-50"
                        style={{ width: columnWidths[col.key] ? `${columnWidths[col.key]}px` : col.defaultWidth ? `${col.defaultWidth}px` : 'auto' }}
                      >
                        {renderFilterInput(col)}
                      </TableHead>
                    ))}
                    {renderRowActions && <TableHead className="py-1.5 bg-slate-50" />}
                  </TableRow>
                )}
              </TableHeader>
              
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell 
                      colSpan={orderedColumns.length + (onSelectionChange ? 1 : 0) + (renderRowActions ? 1 : 0)} 
                      className="text-center py-8 text-slate-500"
                    >
                      No items match your filters
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row, rowIndex) => (
                    <TableRow
                      key={row[rowIdKey]}
                      className={cn(
                        "cursor-pointer transition-colors duration-150 hover:bg-blue-50/60",
                        rowIndex % 2 === 1 && "bg-slate-50/40",
                        selectedIds.has(row[rowIdKey]) && "bg-indigo-50 hover:bg-indigo-100"
                      )}
                      data-testid={`grid-row-${row[rowIdKey]}`}
                    >
                      {onSelectionChange && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(row[rowIdKey])}
                            onCheckedChange={() => toggleSelectRow(row[rowIdKey])}
                            data-testid={`select-row-${row[rowIdKey]}`}
                          />
                        </TableCell>
                      )}
                      {orderedColumns.map((col, colIndex) => {
                        const hoverContent = renderRowHoverContent ? renderRowHoverContent(row, col.key) : null;
                        return (
                          <TableCell
                            key={col.key}
                            className="cursor-pointer"
                            style={{ width: columnWidths[col.key] ? `${columnWidths[col.key]}px` : col.defaultWidth ? `${col.defaultWidth}px` : 'auto', minWidth: '80px' }}
                          >
                            {/* Wrap columns in HoverCard only if renderRowHoverContent returns content */}
                            {hoverContent ? (
                              <HoverCard 
                                openDelay={400} 
                                closeDelay={100}
                                onOpenChange={(open) => {
                                  if (open && onHoverOpen) {
                                    onHoverOpen(row, col.key, open);
                                  }
                                }}
                              >
                                <HoverCardTrigger asChild>
                                  <div 
                                    className="w-full h-full"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (row[rowIdKey]) onRowClick?.(row);
                                    }}
                                  >
                                    {col.render ? col.render(row) : row[col.key] ?? '—'}
                                  </div>
                                </HoverCardTrigger>
                                <HoverCardContent 
                                  side="right" 
                                  align="start" 
                                  className="w-80 p-0 z-50"
                                  data-testid={`hover-card-${row[rowIdKey]}-${col.key}`}
                                >
                                  {hoverContent}
                                </HoverCardContent>
                              </HoverCard>
                            ) : (
                              <div onClick={(e) => {
                                e.stopPropagation();
                                if (row[rowIdKey]) onRowClick?.(row);
                              }}>
                                {col.render ? col.render(row) : row[col.key] ?? '—'}
                              </div>
                            )}
                          </TableCell>
                        );
                      })}
                      {renderRowActions && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {renderRowActions(row)}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-4 border-t border-slate-200 bg-slate-50">
            {/* Left: Info */}
            <div className="flex items-center gap-4 text-sm text-slate-600">
              <span>
                Showing {startIndex + 1} - {Math.min(endIndex, filteredAndSortedData.length)} of {filteredAndSortedData.length}
                {filteredAndSortedData.length !== data.length && ` (filtered from ${data.length})`}
              </span>
              <div className="flex items-center gap-2">
                <span>Rows:</span>
                <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-20 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map(size => (
                      <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Center: Page Numbers */}
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                data-testid="first-page"
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={goToPrevPage}
                disabled={currentPage === 1}
                data-testid="prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              {getPageNumbers().map((page, idx) => (
                page === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-slate-400">...</span>
                ) : (
                  <Button
                    key={page}
                    variant={currentPage === page ? 'default' : 'outline'}
                    size="sm"
                    className={cn(
                      "h-8 w-8 p-0",
                      currentPage === page && "bg-blue-600 hover:bg-blue-700"
                    )}
                    onClick={() => setCurrentPage(page)}
                    data-testid={`page-${page}`}
                  >
                    {page}
                  </Button>
                )
              ))}
              
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                data-testid="next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                data-testid="last-page"
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Right: Jump to Page */}
            <form onSubmit={handleJumpToPage} className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Go to:</span>
              <Input
                type="number"
                min="1"
                max={totalPages}
                value={jumpToPage}
                onChange={(e) => setJumpToPage(e.target.value)}
                placeholder="#"
                className="w-16 h-8 text-center"
                data-testid="jump-to-page"
              />
              <Button type="submit" variant="outline" size="sm" className="h-8">
                Go
              </Button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
